import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getActiveInvestorJourney } from '../services/investorJourneyScope';
import { getSAProxyInvestorId } from '../services/saProxyAdapter';
import type { InvestorDashboardDto } from '../services/investor.service';
import { isStepDone, JOURNEY_STEPS, nextStepPathAfter, toServiceAgentPath, type JourneyStepKey } from '../utils/journeySteps';

interface Options {
  /** The step this page represents. */
  step: JourneyStepKey;
  /** Dashboard the page currently holds (drives the "done" rule). */
  dashboardData: InvestorDashboardDto | null;
  /**
   * Silent refresh: re-read whatever the page needs (requirements + dashboard) WITHOUT
   * toggling the page's loading state, and return the fresh dashboard. Must not throw.
   */
  refresh: () => Promise<InvestorDashboardDto | null>;
  /** While true (e.g. an upload in flight) the poll is skipped so it can never race an action. */
  paused?: boolean;
  /** Poll interval while the step is not yet done. Default 60 s. */
  intervalMs?: number;
}

/**
 * Keeps a journey-step page current and moves the investor on when the step completes:
 *  - re-reads status when the tab regains focus/visibility and on a gentle poll (the
 *    requirements endpoint already syncs each document's status from Dataverse on read, so an
 *    SP approval shows up within one interval — no backend change);
 *  - when the step flips from NOT done → done while the page is open, announces it and
 *    navigates to the next incomplete step via the same rule the stepper highlights with.
 * Never fires on a page opened for an already-completed step (revisiting stays possible),
 * and stops polling once the step is done.
 */
export function useJourneyStepAdvance({ step, dashboardData, refresh, paused, intervalMs = 60_000 }: Options): void {
  const navigate = useNavigate();
  const location = useLocation();
  // A deliberate visit (stepper click, "back to" link) carries state.visit = true and must
  // never be bounced; an implicit arrival (refresh, redirect, bookmark) on a step that is
  // already complete is carried forward to the first step still to do.
  const deliberate = !!(location.state as { visit?: boolean } | null)?.visit;
  const wasDone = useRef<boolean | null>(null);
  const advanced = useRef(false);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const pausedRef = useRef(!!paused);
  pausedRef.current = !!paused;

  // Baseline: remember whether the step was already done when the page opened.
  useEffect(() => {
    if (dashboardData && wasDone.current === null) wasDone.current = isStepDone(step, dashboardData);
  }, [dashboardData, step]);

  // Advance once, either on the transition NOT done → done while the page is open, or when
  // an implicit arrival finds the step already done. Never when nothing later remains
  // (nextStepPathAfter then returns this step's own path) and never on a deliberate visit.
  useEffect(() => {
    if (!dashboardData || wasDone.current === null || advanced.current) return;
    const done = isStepDone(step, dashboardData);
    const transitioned = !wasDone.current && done;
    const arrivedOnDone = wasDone.current && done && !deliberate;
    if (!transitioned && !arrivedOnDone) return;
    const me = JOURNEY_STEPS.find((s) => s.key === step);
    const target = nextStepPathAfter(step, dashboardData, getActiveInvestorJourney());
    if (!me || target === me.path) return;            // nothing further to do: stay here
    advanced.current = true;
    const nextLabel = JOURNEY_STEPS.find((s) => s.path === target || target.startsWith(s.path))?.label;
    if (transitioned) {
      toast.success(nextLabel ? `${me.label} completed — continuing to ${nextLabel}` : `${me.label} completed`);
    }
    const sa = getSAProxyInvestorId();
    navigate(sa !== null ? toServiceAgentPath(target, sa) : target, { replace: arrivedOnDone });
  }, [dashboardData, step, navigate, deliberate]);

  // Poll + focus/visibility refresh while the step is not done.
  useEffect(() => {
    if (!dashboardData || isStepDone(step, dashboardData)) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || pausedRef.current || document.visibilityState === 'hidden') return;
      try { await refreshRef.current(); } catch { /* silent — next tick retries */ }
    };
    const id = window.setInterval(tick, intervalMs);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dashboardData, step, intervalMs]);
}
